import $ from 'jquery';
import type {
    EventDayShape,
    LayoutMode,
} from './Types';
import {
    CALENDAR_STYLE_MAP,
    COMPONENT_BODY_POSITION_MAP,
    DATE_SHAPE_MAP,
    ViewDataEventsSlidersSchema,
    type ViewDataEventsSlider,
} from './Schemas';
import {
    type EventSliderConfiguration,
    type SliderConfiguration,
    SLIDER_CONFIGURATIONS,
} from '@classic/Components/EventsSlider/DisplayModule/EventSliderViewConfig';
import EventSliderView from '@classic/Components/EventsSlider/DisplayModule/EventSliderView';
import EventsRepository from '@classic/Components/EventsSlider/Persistence/EventsRepository';
import FacebookPage from '@classic/Components/EventsSlider/Components/FacebookPage';
import '@classic/Components/EventsSlider/scss/StackEventsSlider.scss';

const eventsRepository = new EventsRepository();
const stackItemCssMap = new Map<string, Record<string, string>>();
const viewDataCache = new Map<string, ViewDataEventsSlider>();

const getViewData = (stackItemID: string | number): ViewDataEventsSlider | null =>
    viewDataCache.get(stackItemID.toString()) ?? null;

const CALENDAR_STYLE_CSS_CLASS: Record<number, string> = {
    [CALENDAR_STYLE_MAP.FOUR_EVENTS]: 'calendar-events-style-four-events',
    [CALENDAR_STYLE_MAP.SIX_EVENTS]: 'calendar-events-style-six-events',
    [CALENDAR_STYLE_MAP.FOUR_EVENTS_FULL_CALENDAR]: 'calendar-events-style-calendar-and-events',
    [CALENDAR_STYLE_MAP.FOUR_EVENTS_FACEBOOK_FEED]: 'calendar-events-style-facebook-and-events',
    [CALENDAR_STYLE_MAP.FULL_CALENDAR]: 'calendar-events-style-full-calendar',
    [CALENDAR_STYLE_MAP.GRID]: 'calendar-events-style-grid',
};

const SLIDER_CONFIG_BY_STYLE: Record<number, { default: SliderConfiguration; stacked?: SliderConfiguration }> = {
    [CALENDAR_STYLE_MAP.GRID]: { default: SLIDER_CONFIGURATIONS.MEAL_EVENTS_GRID },
    [CALENDAR_STYLE_MAP.FOUR_EVENTS]: { default: SLIDER_CONFIGURATIONS.FOUR_EVENTS, stacked: SLIDER_CONFIGURATIONS.FOUR_EVENTS_STACKED },
    [CALENDAR_STYLE_MAP.SIX_EVENTS]: { default: SLIDER_CONFIGURATIONS.SIX_EVENTS, stacked: SLIDER_CONFIGURATIONS.SIX_EVENTS_STACKED },
    [CALENDAR_STYLE_MAP.FOUR_EVENTS_FACEBOOK_FEED]: { default: SLIDER_CONFIGURATIONS.FOUR_EVENTS_FACEBOOK_FEED },
    [CALENDAR_STYLE_MAP.FOUR_EVENTS_FULL_CALENDAR]: { default: SLIDER_CONFIGURATIONS.FOUR_EVENTS_FULL_CALENDAR },
};

/**
 * Stacked layout: date, info and time stack vertically.
 * Inline layout (default): date, info and time display side by side.
 */
const STACKED_LAYOUT_CONFIGS: readonly SliderConfiguration[] = [
    SLIDER_CONFIGURATIONS.FOUR_EVENTS,
    SLIDER_CONFIGURATIONS.MEAL_EVENTS_GRID,
];

const EVENT_DAY_SHAPE: Record<number, EventDayShape> = {
    [DATE_SHAPE_MAP.RECTANGLE]: 'rectangle',
    [DATE_SHAPE_MAP.CIRCLE]: 'circle',
};

const getCalendarStyleCssClass = (calendarStyle: number): string =>
    CALENDAR_STYLE_CSS_CLASS[calendarStyle] ?? '';

const getSliderConfiguration = (calendarStyle: number, hasComponentBody: boolean): SliderConfiguration | null => {
    const entry = SLIDER_CONFIG_BY_STYLE[calendarStyle];
    if (!entry) return null;
    return (hasComponentBody && entry.stacked) ? entry.stacked : entry.default;
};

const getEventComponentLayoutMode = (sliderConfiguration: SliderConfiguration | null): LayoutMode =>
    sliderConfiguration && STACKED_LAYOUT_CONFIGS.includes(sliderConfiguration) ? 'stacked' : 'inline';

/**
 * Adjusts the stack item settings coming from PHP to the window object.
 * Sets data attributes for non-CSS settings like layout and shape.
 * @see EventsSliderModel::stackItemSettings() in PHP
 * @param $root - The root jQuery element of the stack item.
 * @param stackItemID - The ID of the stack item.
 */
const initializeCalendarContainerSettings = ($root: JQuery, stackItemID: string): Record<string, string> | null => {
    const scriptDataElem: HTMLScriptElement | null = document.querySelector(
        `script[type="application/json"][data-view-data-events-sliders="${stackItemID}"]`,
    );

    const calendarSettings: ViewDataEventsSlider | null = scriptDataElem ? _parseScriptViewData(scriptDataElem) : null;
    if (!calendarSettings) {
        return null;
    }

    // build the cache of view data for the stack item
    viewDataCache.set(stackItemID.toString(), calendarSettings);

    // remove the script element from the DOM
    scriptDataElem?.remove();

    _applyCssClassesToContainer($root, calendarSettings);
    _applyComponentContentBreakoutClass($root.find('.ss-component-content'), calendarSettings);

    const componentInnerView = $root.find('.calendar-events-view-container');
    if (componentInnerView.length > 0) {
        _applyClassesToEventSliderContainer(componentInnerView, calendarSettings);
    }

    return Object.fromEntries(
        Object.entries(calendarSettings).filter(([key]) => key.startsWith('--ss-calendar-events-'))
    ) as Record<string, string>;
};

/**
 * Parses and validates the JSON content of a `<script type="application/json">` element
 * against the {@link ViewDataEventsSlidersSchema}.
 */
const _parseScriptViewData = (scriptDataElem: HTMLScriptElement): ViewDataEventsSlider | null => {
    if (!scriptDataElem || !scriptDataElem.textContent) {
        return null;
    }
    try {
        const validationResult = ViewDataEventsSlidersSchema.safeParse(JSON.parse(scriptDataElem.textContent));
        if (!validationResult.success) {
            console.error('Invalid view data events sliders:', validationResult.error, validationResult);
            return null;
        }
        return validationResult.data as ViewDataEventsSlider;
    } catch (error) {
        console.error('Error parsing view data events sliders:', error);
        return null;
    }
};

/**
 * Apply the CSS classes to the container.
 * @param $root - The `<section>` element that contains the calendar events.
 * @param calendarSettings - The calendar settings.
 */
const _applyCssClassesToContainer = (
    $root: JQuery,
    calendarSettings: ViewDataEventsSlider,
): void => {
    if (calendarSettings.editable) {
        $root.addClass('editme');
    }
    if (!calendarSettings.isStackItemVisible) {
        $root.addClass('ss-hidden-component');
    }
    if (calendarSettings.hasBackgroundBreakout) {
        $root.addClass('break-out');
    }
    if (calendarSettings.hasStackBackground) {
        $root.addClass('ss-has-bg');
    }
    if (calendarSettings.calendarStyle === CALENDAR_STYLE_MAP.GRID) {
        if (calendarSettings.hasGridTopBg) {
            $root.addClass('ss-has-top-bg');
        }
        if (calendarSettings.hasGridBottomBg) {
            $root.addClass('ss-has-bottom-bg');
        }
    }
};

/**
 * Apply the CSS classes to the inner container of the calendar.
 * @param $root - The inner container of the calendar.
 * @param calendarSettings - The calendar settings.
 */
const _applyComponentContentBreakoutClass = (
    $root: JQuery,
    calendarSettings: ViewDataEventsSlider,
): void => {
    if (calendarSettings.hasBackgroundBreakout && !calendarSettings.hasContentBreakout) {
        $root.addClass('background-break-out');
    }
    if (!calendarSettings.hasBackgroundBreakout && calendarSettings.hasContentBreakout) {
        $root.addClass('break-out');
    }
};

const _applyClassesToEventSliderContainer = (
    $root: JQuery<HTMLElement>,
    calendarSettings: ViewDataEventsSlider,
): void => {
    if (calendarSettings.hasComponentBody && calendarSettings.componentBodyPosition === COMPONENT_BODY_POSITION_MAP.LEFT) {
        $root.addClass('calendar-events-style-component-body-left');
    }
    if (calendarSettings.hasComponentBody && calendarSettings.componentBodyPosition === COMPONENT_BODY_POSITION_MAP.RIGHT) {
        $root.addClass('calendar-events-style-component-body-right');
    }

    if (!calendarSettings.isStackItemVisible) {
        $root.addClass('stack_off');
    }

    if (calendarSettings.calendarDateShape === DATE_SHAPE_MAP.RECTANGLE) {
        $root.addClass('calendar-events-shape-rectangle');
    }
    if (calendarSettings.calendarDateShape === DATE_SHAPE_MAP.CIRCLE) {
        $root.addClass('calendar-events-shape-circle');
    }

    $root.addClass(getCalendarStyleCssClass(calendarSettings.calendarStyle));
    if (calendarSettings.calendarStyle === CALENDAR_STYLE_MAP.GRID) {
        $root.attr('data-calendar-style', 'grid');
    }
};

const _initializeEventsSliders = (
    container: HTMLElement,
    settings: ViewDataEventsSlider,
): void => {
    const config = getSliderConfiguration(
        settings.calendarStyle,
        settings.hasComponentBody
    );
    if (!config) {
        return;
    }
    const eventSliderConfig: EventSliderConfiguration = {
        calendarId: settings.calendarApiID,
        cssVariables: stackItemCssMap.get(settings.itemID.toString()) || {},
        sliderConfiguration: config,
        displayEvents: settings.numberOfEvents,
        originationDate: new Date(),
        layoutMode: getEventComponentLayoutMode(config),
        eventDayShape: EVENT_DAY_SHAPE[settings.calendarDateShape] ?? 'rectangle',
        isMealCalendar: settings.isMealCalendar ?? false,
    };

    const eventSlider = new EventSliderView(
        container,
        eventsRepository,
        eventSliderConfig,
    );
    eventSlider.initialize();
};

const _initializeFacebookPage = (container: HTMLElement, settings: ViewDataEventsSlider): void => {
    if (!settings.facebookFeedURL) {
        return;
    }
    const facebookPage = new FacebookPage();
    facebookPage.setAttribute('page-url', settings.facebookFeedURL);
    $(container).append(facebookPage);
};

const _initializeFullCalendar = async (container: HTMLElement, settings: ViewDataEventsSlider): Promise<void> => {
    // dynamically import the FullCalendarView so it gets split into a separate chunk
    const { default: FullCalendarView } = await import('@classic/Components/EventsSlider/DisplayModule/FullCalendarView');
    new FullCalendarView(
        container,
        settings.calendarApiID,
        settings.standaloneCalendar ?? false,
        settings['--ss-calendar-events-calendar-link-background-color'] ?? null
    );
};

//
// gather all calendars and adjust the settings for each container
document.querySelectorAll('.ss-calendar-events__v2').forEach((element: Element) => {
    const $element = $(element) as JQuery<HTMLElement>;
    const stackItemID = $element.data('stackitem-id');
    if (!stackItemID) {
        return;
    }

    const cssVariables = initializeCalendarContainerSettings($element, stackItemID);
    // if the CSS variables are not empty, add them to the map of stackItem and CSS variables
    if (cssVariables) {
        // apply the css variables to the stack item container
        $element.css(cssVariables);
        stackItemCssMap.set(stackItemID.toString(), cssVariables);
    }

    const settings = getViewData(stackItemID);
    const viewContainer = $element.find('.calendar-events-view-container').get(0);
    if (viewContainer && settings) {
        _initializeEventsSliders(viewContainer, settings);
    }

    const facebookPageContainer = $element.find('.stack-item-events-facebook-container').get(0);
    if (facebookPageContainer && settings) {
        _initializeFacebookPage(facebookPageContainer, settings);
    }

    const fullCalendarContainer = $element.find('.full-calendar-container').get(0);
    if (fullCalendarContainer && settings) {
        _initializeFullCalendar(fullCalendarContainer, settings).catch(console.error);
    }
});
